import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { Observable, of, ReplaySubject } from 'rxjs';
import { tap } from 'rxjs/operators';

import { MtnaLogger } from '@mtna/lib-ui';
import { Snippet, SnippetGenerator } from '@mtna/rds-packaging-ui';

import { RdsApiSnippetQueryType } from '../models/api-snippet-query-type.model';
import { serializeBaseUrlParam } from '../models/parameters.model';
import { RdsRawApiQuery } from '../models/raw-api-query.model';
import { SelectParameters, serializeSelectOptionsUrlParams } from '../models/select-parameters.model';
import { serializeTabulationOptionsUrlParams, TabulationParameters } from '../models/tabulation-parameters.model';
import { RdsApiService } from './rds-api.service';

@Injectable({ providedIn: 'root' })
export class RdsSnippetService {
  /** base url for the snippet service */
  get SNIPPET_URL(): string {
    return `${this.apiService.getApiUrl()}/snippet`;
  }

  /** url for the selected data product */
  protected get DATA_PRODUCT_URL(): string {
    return `${this.SNIPPET_URL}/${this.apiService.getCatalogId()}/${this.apiService.getDataProductId()}`;
  }

  protected _snippetGeneratorsCache$ = new ReplaySubject<SnippetGenerator[]>(1);
  protected _snippetGeneratorsRequest$: Observable<SnippetGenerator[]>;
  protected _selectCache = new Map<string, any>();
  protected _tabulateCache = new Map<string, any>();

  constructor(
    protected apiService: RdsApiService,
    private http: HttpClient,
    protected logger: MtnaLogger,
    private sanitizer: DomSanitizer
  ) {}

  /**
   * Snippet generators can be used to create calls in external libraries that
   * when run, will get the data set represented by a provided query. This end
   * point exposes the available snippet generators and their details.
   * @remarks This method uses caching, returns a hot observable.
   * @param refresh forces a cache refresh, will update all subscribers. Defaults to false.
   * @returns all available snippet generators
   */
  getSnippetGenerators(refresh: boolean = false): Observable<SnippetGenerator[]> {
    if (refresh || !this._snippetGeneratorsRequest$) {
      this.logger.debug(`[RdsSnippetService] getSnippetGenerators - Fetching, ${refresh ? 'forced refresh' : 'not in cache'}`);
      this._snippetGeneratorsRequest$ = this.http.get<SnippetGenerator[]>(`${this.SNIPPET_URL}/generators`);
      this._snippetGeneratorsRequest$.subscribe(
        (result) => this._snippetGeneratorsCache$.next(result),
        (err) => this._snippetGeneratorsCache$.error(err)
      );
    }
    return this._snippetGeneratorsCache$.asObservable();
  }

  /**
   * Requests a snippet of code in the requested library to recreate the
   * provided select query.
   *
   * @remarks This method uses caching, returns a cold observable.
   * @param params various select parameters
   * @param library the name of the library for the requested code snippet
   * @param version the version of the library for the requested code snippet
   * @param refresh forces a cache refresh, will fetch a new value from the API. Defaults to false.
   * @returns The code snippet for the specified select query applicable to the given version of the library
   */
  generateSelectSnippet(params: SelectParameters, library: string, version: string, refresh: boolean = false): Observable<Snippet> {
    const libraryParam = serializeBaseUrlParam('library', library);
    const versionParam = serializeBaseUrlParam('version', version);
    const baseUrlParam = serializeBaseUrlParam('baseUrl', this._normalizeBaseUrl());
    const selectParams = serializeSelectOptionsUrlParams(params);

    // Full API path
    const path = `${this.DATA_PRODUCT_URL}/select?` + libraryParam + versionParam + baseUrlParam + selectParams;
    // Use the path as the map key
    const mapKey = path;

    if (refresh) {
      this.logger.debug(`[RdsSnippetService] generateSelectSnippet - Refresh requested, removing ${mapKey} from cache`);
      this._selectCache.delete(mapKey);
    }
    if (this._selectCache.has(mapKey)) {
      this.logger.debug(`[RdsSnippetService] generateSelectSnippet - Returning ${mapKey} from cache`);
      return of(this._selectCache.get(mapKey));
    }
    this.logger.debug(`[RdsSnippetService] generateSelectSnippet - Fetching, ${mapKey} not in cache`);
    return this.http.get<Snippet>(path).pipe(tap((ds) => this._selectCache.set(mapKey, ds)));
  }

  /**
   * Requests a snippet of code in the requested library to recreate the
   * provided tabulate query.
   *
   * @remarks This method uses caching, returns a cold observable.
   * @param params various tabulation parameters
   * @param library the name of the library for the requested code snippet
   * @param version the version of the library for the requested code snippet
   * @param refresh forces a cache refresh, will fetch a new value from the API. Defaults to false.
   * @returns The code snippet for the specified tabulate query applicable to the given version of the library
   */
  generateTabulateSnippet(params: TabulationParameters, library: string, version: string, refresh: boolean = false): Observable<Snippet> {
    const libraryParam = serializeBaseUrlParam('library', library);
    const versionParam = serializeBaseUrlParam('version', version);
    const baseUrlParam = serializeBaseUrlParam('baseUrl', this._normalizeBaseUrl());
    const tabulateParams = serializeTabulationOptionsUrlParams(params);

    // Full API path
    const path = `${this.DATA_PRODUCT_URL}/tabulate?` + libraryParam + versionParam + baseUrlParam + tabulateParams;
    // Use the path as the map key
    const mapKey = path;

    if (refresh) {
      this.logger.debug(`[RdsSnippetService] generateTabulateSnippet - Refresh requested, removing ${mapKey} from cache`);
      this._tabulateCache.delete(mapKey);
    }
    if (this._tabulateCache.has(mapKey)) {
      this.logger.debug(`[RdsSnippetService] generateTabulateSnippet - Returning ${mapKey} from cache`);
      return of(this._tabulateCache.get(mapKey));
    }
    this.logger.debug(`[RdsSnippetService] generateTabulateSnippet - Fetching, ${mapKey} not in cache`);
    return this.http.get<Snippet>(path).pipe(tap((ds) => this._tabulateCache.set(mapKey, ds)));
  }

  /**
   * Create the html representation and raw query path.
   *
   * @param type the type of query, SELECT or TABULATE
   * @param params the query parameters
   * @returns an html representation and the raw RDS API path
   */
  generateRawApiQuery(type: RdsApiSnippetQueryType, params: SelectParameters | TabulationParameters): RdsRawApiQuery {
    // Base url and query type (select/tabulate)
    const baseUrl = `${this.apiService.getApiUrl()}/query/${this.apiService.getCatalogId()}/${this.apiService.getDataProductId()}/${type.toLowerCase()}?`;
    // Query-type specific query parameters
    const queryParams =
      type === 'SELECT' ? serializeSelectOptionsUrlParams(params) : serializeTabulationOptionsUrlParams(params as TabulationParameters);

    // Generate html
    const space = 8;
    let html = '';
    html += `${this.apiService.getApiUrl()}/query/` + '<br />';
    html += `<span style="display:inline-block; width: ${space * 2}px"></span>${this.apiService.getCatalogId()}/` + '<br />';
    html += `<span style="display:inline-block; width: ${space * 2}px"></span>${this.apiService.getDataProductId()}/` + '<br />';
    html += `<span style="display:inline-block; width: ${space * 2}px"></span>${type.toLowerCase()}`;

    // Split them on the ampersand to get individual
    if (queryParams) {
      const individualQueryParams: string[] = queryParams.split('&');
      if (individualQueryParams.length) {
        html += `<span class="rds-snippet-secondary-color" style="margin-left:${space}px;">?</span>` + '<br />';
      }

      // Render the parameters
      // - Display as a table to keep parameters aligned
      html += `<table style="margin-left:${space * 4}px; border: none; border-spacing: unset; border-collapse: collapse;">`;
      for (let i = 0; i < individualQueryParams.length; i++) {
        // The serialize method encodes the parameters, so we need to decode here
        const decoded: string = decodeURIComponent(individualQueryParams[i]);
        const nameAndParam = decoded
          // Split on the first occurance of the equal sign
          .split(/=(.+)/)
          // Filter out empty strings
          .filter((s) => !!s);
        if (nameAndParam.length === 2) {
          html += '<tr>';
          html += `<td><em>${nameAndParam[0]}</em></td>`;
          html += `<td style="padding: 0 ${space}px">=</td>`;
          html += `<td>${nameAndParam[1]}`;
          if (i < individualQueryParams.length - 1) {
            // If there is another parameter, add the ampersand
            html += `<span class="rds-snippet-secondary-color" style="margin-left:${space}px;">&</span>`;
          }
          html += '</td></tr>';
        }
      }
      html += '</table>';
    }
    return new RdsRawApiQuery(this.sanitizer.bypassSecurityTrustHtml(html), `${baseUrl}${queryParams}`);
  }

  /**
   * Normalize the api url into the expected part for snippet api methods.
   * They expect the url to end with "/rds", not "/rds/api".
   *
   * @returns the normalized base url for snippet api methods
   */
  protected _normalizeBaseUrl(): string {
    return this.apiService.apiUrl.endsWith('/api')
      ? this.apiService.apiUrl.substr(0, this.apiService.apiUrl.length - 4)
      : this.apiService.apiUrl;
  }
}
